import AbstractShape from "@/Models/Shapes/AbstractShape";
import { Text } from "@/Models/Shapes/Text";
import { capitalize } from "@/functions/strings";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";

export interface ValidatorObject {
	field: string;
	dimension: string;
	instance?: AbstractShape;
	customMessage?: string;
	minimum?: number;
	minimums?: object;
	maximum?: number;
	maximums?: object;
	isCutout?: boolean;
	value?: unknown;
}

/**
 * Abstract validator class.
 */
export abstract class AbstractValidator {
	field: string;
	dimension: string;
	instance?: AbstractShape;
	customMessage?: string;
	minimum?: number;
	minimums?: object;
	maximum?: number;
	maximums: object;
	isCutout?: boolean;
	value?: unknown;

	/**
	 * Constructor.
	 *
	 * @protected
	 * @param {ValidatorObject} options
	 */
	protected constructor( options: ValidatorObject ) {
		Object.assign( this, options );
	}

	/**
	 * Gets the field the validator applies to.
	 *
	 * @returns {string} The field.
	 */
	getField(): string {
		return this.getPrefix() + this.field;
	}

	/**
	 * Gets the custom message, if set.
	 *
	 * @returns {string} The custom message.
	 */
	getCustomMessage(): string {
		if ( this.customMessage ) {
			return this.customMessage;
		}
	}

	getPrefix(): string {
		return this.isCutout ? 'cutout_' : '';
	}

	getDimension(): string {

		// Get the mappings if they exist.
		if ( ! isUndefined( this.instance ) && ! isEmpty( this.instance.getLabelMappings() ) ) {
			return this.instance.getLabelMappings()[ this.dimension ] ?? this.dimension;
		}

		return ( this.dimension === 'desired_length' && this.instance instanceof Text ) ? this.instance.getDesiredDimension() : this.dimension;
	}

	getDimensionMethodKey(): string {
		return this.dimension.split( '_' ).map( dimension_part => capitalize( dimension_part ) ).join( '' );
	}

	getDimensionValue(): number {
		const value = this.instance[ `get${ this.getDimensionMethodKey() }` ]() ?? null;

		if ( ( this.getDimension() === 'number_of_points' && value ) || this.getDimension() === 'side_amount' && value ) {
			return value;
		}

		return ( value instanceof Length ) ? value.in_cm() : 0;
	}

	getDimensionLabel(): string {
		return this.getValidatorTranslationStrings().dimensions.labels[ this.getDimension() ];
	}

	getValidatorTranslationStrings(): Window['measurementSelectorVariables']['i18n']['validators'] {
		return window.measurementSelectorVariables.i18n.validators;
	}

	getIsCutout(): boolean {
		return this.isCutout;
	}
}
